import { useMemo, useSyncExternalStore } from 'react';

export function useChartTooltip(opts) {
  const store = useMemo(() => createTooltipStore(), []);
  const tooltip = useSyncExternalStore(store.subscribe, store.get, store.get);

  const external = useMemo(() => makeExternalTooltip(store, opts), [store, opts]);
  return { tooltip, external };
}

function createTooltipStore(initial = { visible: false, items: [] }) {
  let state = initial;
  const listeners = new Set();

  const get = () => state;
  const subscribe = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  const shallowChanged = (a, b) => {
    if (a === b) return false;
    // Compare only fields we set
    const keys = [
      'visible',
      'anchorX',
      'anchorY',
      'attachSide',
      'label',
      'total',
      'datasetIndex',
      'dataIndex',
      'canvasLeft',
      'canvasTop',
    ];
    for (const k of keys) if (a?.[k] !== b?.[k]) return true;
    if ((a?.items?.length || 0) !== (b?.items?.length || 0)) return true;
    for (let i = 0; i < (b.items?.length || 0); i++) {
      const p = a.items?.[i] || {};
      const n = b.items[i];
      if (
        p.datasetIndex !== n.datasetIndex ||
        p.dataIndex !== n.dataIndex ||
        p.value !== n.value ||
        p.datasetLabel !== n.datasetLabel
      )
        return true;
    }
    return false;
  };

  const set = (next) => {
    if (!shallowChanged(state, next)) return;
    state = next;
    listeners.forEach((l) => l());
  };

  return { get, set, subscribe };
}

function makeExternalTooltip(store, opts = {}) {
  return ({ chart, tooltip }) => {
    if (!tooltip || tooltip.opacity === 0) {
      const prev = store.get();
      if (prev.visible) store.set({ ...prev, visible: false });
      return;
    }

    const rect = chart.canvas.getBoundingClientRect();
    const area = chart.chartArea || {
      top: 0,
      left: 0,
      right: rect.width,
      bottom: rect.height,
    };

    const caretX = tooltip.caretX ?? (area.left + area.right) / 2;
    const caretY = tooltip.caretY ?? (area.top + area.bottom) / 2;

    // how much room in each direction *inside the chart area*
    const spaceTop = caretY - area.top;
    const spaceBottom = area.bottom - caretY;
    const spaceLeft = caretX - area.left;
    const spaceRight = area.right - caretX;

    let attachSide = 'top'; // 'top' | 'bottom' | 'left' | 'right'

    const maxVertical = Math.max(spaceTop, spaceBottom);
    const maxHorizontal = Math.max(spaceLeft, spaceRight);

    if (maxVertical >= maxHorizontal) {
      // prefer vertical when it's not worse than horizontal
      attachSide = spaceTop > spaceBottom ? 'top' : 'bottom';
    } else {
      attachSide = spaceLeft > spaceRight ? 'left' : 'right';
    }

    if (opts.forceDirection) {
      attachSide = opts.forceDirection;
    }

    let transform;
    let offsetXMultiplier = 0;
    let offsetYMultiplier = 0;
    switch (attachSide) {
      case 'top':
        transform = 'translate(-50%, -100%)';
        offsetYMultiplier = -1;
        break;
      case 'bottom':
        transform = 'translate(-50%, 0)';
        offsetYMultiplier = 1;
        break;
      case 'left':
        transform = 'translate(-100%, -50%)';
        offsetXMultiplier = -1;
        break;
      case 'right':
        transform = 'translate(0, -50%)';
        offsetXMultiplier = 1;
        break;
    }

    const items = (tooltip.dataPoints || []).map((dp) => {
      const color =
        dp.element?.options?.backgroundColor ||
        (Array.isArray(dp.dataset?.borderColor) ? dp.dataset.borderColor[dp.dataIndex] : dp.dataset?.borderColor) ||
        (Array.isArray(dp.dataset?.backgroundColor)
          ? dp.dataset.backgroundColor[dp.dataIndex]
          : dp.dataset?.backgroundColor) ||
        undefined;

      return {
        datasetIndex: dp.datasetIndex,
        dataIndex: dp.dataIndex,
        datasetLabel: dp.dataset?.label,
        value: dp.formattedValue ?? dp.raw,
        raw: dp.raw,
        color,
      };
    });

    const sharedLabel = (tooltip.title && tooltip.title[0]) || tooltip.dataPoints?.[0]?.label || undefined;

    const total = items.reduce((sum, it) => {
      const n = typeof it.raw === 'number' ? it.raw : Number(it.raw);
      return sum + (isNaN(n) ? 0 : n);
    }, 0);

    store.set({
      visible: true,
      anchorX: caretX, // caret in canvas coords
      anchorY: caretY,
      canvasLeft: rect.left,
      canvasTop: rect.top,
      attachSide, // where tooltip should go relative to caret
      transform,
      offsetYMultiplier,
      offsetXMultiplier,
      label: sharedLabel,
      items,
      total,
      datasetIndex: items[0]?.datasetIndex ?? null,
      dataIndex: items[0]?.dataIndex ?? null,
    });
  };
}
